import { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { Select } from './Select'
import { BOOLEAN_FILTERS, defaultFilter, edit, FILTER_ORDER, flatEdit, flatFilter, isFlat, linkKeysOf, PERIOD_PRESETS, type Side, withLinkKeys } from './rule'
import type { Filter, FilterInfo, FilterType, Period, Rule } from './types'

export interface ListOption {
  id: string
  name: string
}

interface Props {
  rule: Rule
  onChange: (rule: Rule) => void
  filters: FilterInfo[]
  /** The Account's OnlyFans lists for "In list"; null while loading. */
  lists: ListOption[] | null
  /** A Dynamic list may not filter on its own OnlyFans list. */
  ownListId?: string | null
  /** Why `lists` could not be read, and names known from the database. */
  listsMeta?: ListsMeta
  /** The Account's tracking links for "Tracking link" (ADR 0032); null while loading. */
  links?: ListOption[] | null
  linksMeta?: ListsMeta
}

export interface ListsMeta {
  error?: string
  /** The options are still being read from OnlyFans; more may arrive. */
  syncing?: boolean
  names?: Record<string, string>
}

/**
 * Include groups (any may match; every filter inside one must) and exclude
 * groups (a fan matching any is left out). Edits are immutable Rule values.
 *
 * A rule that fits (see isFlat) shows every filter as its own row with a switch;
 * "or" groups need the advanced group editor.
 */
export function RuleEditor({ rule, onChange, filters, lists, ownListId, listsMeta, links = null, linksMeta }: Props) {
  const { t } = useTranslation('dynamicLists')
  const [advanced, setAdvanced] = useState(() => !isFlat(rule))
  const flat = !advanced && isFlat(rule)
  const common = { rule, onChange, filters, lists, ownListId, listsMeta, links, linksMeta }
  return (
    <div className="dl-rule">
      {flat ? (
        <>
          <FlatSide side="include" {...common} />
          <FlatSide side="exclude" {...common} />
        </>
      ) : (
        <>
          {!isFlat(rule) && <p className="dl-notice dl-notice--warn">{t('editor.advancedOnly')}</p>}
          <RuleSide side="include" {...common} />
          <RuleSide side="exclude" {...common} />
        </>
      )}
      {(flat || isFlat(rule)) && (
        <button type="button" className="dl-link dl-rule-mode" onClick={() => setAdvanced(!advanced)}>
          <FeatherIcon name={flat ? 'git-branch' : 'list'} size={13} /> {flat ? t('editor.advanced') : t('editor.simple')}
        </button>
      )}
      <p className="dl-hint">{t('facts.hint')}</p>
    </div>
  )
}

/** Every filter type as a row: a switch turns it on for this side, and its values stay editable either way. */
function FlatSide({ side, rule, onChange, filters, lists, ownListId, listsMeta, links, linksMeta }: Props & { side: Side }) {
  const { t } = useTranslation('dynamicLists')
  const info = new Map(filters.map((f) => [f.type, f]))
  // Values of a row switched off, so switching it back on restores them.
  const drafts = useRef(new Map<FilterType, Filter>())
  return (
    <section className={`dl-flat dl-flat--${side}`} aria-label={t(`editor.flat.${side}`)}>
      <header className="dl-flat-head">
        <h3 className="dl-h3">
          <FeatherIcon name={side === 'include' ? 'check-circle' : 'x-circle'} size={15} /> {t(`editor.flat.${side}`)}
        </h3>
        <span className="dl-muted">{t(`editor.flat.${side}Hint`)}</span>
      </header>
      {FILTER_ORDER.map((type) => {
        const current = flatFilter(rule, side, type)
        const filter = current ?? drafts.current.get(type) ?? defaultFilter(type)
        // Editing a value of a row that is off turns it on: the edit is what the user wants.
        const set = (next: Filter) => {
          drafts.current.set(type, next)
          onChange(flatEdit.set(rule, side, next))
        }
        return (
          <FlatRow
            key={type}
            filter={filter}
            on={Boolean(current)}
            available={info.get(type)?.available !== false}
            lists={lists}
            ownListId={ownListId}
            listsMeta={listsMeta}
            links={links ?? null}
            linksMeta={linksMeta}
            onChange={set}
            onToggle={(on) => (on ? set(filter) : onChange(flatEdit.remove(rule, side, type)))}
          />
        )
      })}
    </section>
  )
}

function FlatRow({
  filter,
  on,
  available,
  onToggle,
  ...params
}: {
  filter: Filter
  on: boolean
  available: boolean
  lists: ListOption[] | null
  ownListId?: string | null
  listsMeta?: ListsMeta
  links: ListOption[] | null
  linksMeta?: ListsMeta
  onChange: (f: Filter) => void
  onToggle: (on: boolean) => void
}) {
  const { t } = useTranslation('dynamicLists')
  const id = useId()
  const help = t(`filterHelp.${filter.type}`, { defaultValue: '' })
  return (
    <div className={`dl-frow ${on ? 'is-on' : ''} ${available ? '' : 'is-unavailable'}`}>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={on}
        className="dl-switch"
        disabled={!available && !on}
        onClick={() => onToggle(!on)}
      >
        <span className="dl-switch-knob" />
      </button>
      <label htmlFor={id} className="dl-frow-label">
        {t(`filter.${filter.type}`)}
        {!available && <span className="dl-muted"> ({t('editor.comingSoon')})</span>}
      </label>
      {available && (
        <div className="dl-frow-params">
          <FilterParams filter={filter} {...params} />
        </div>
      )}
      {help && (
        <span className="dl-frow-help" title={help} aria-label={help} role="img">
          <FeatherIcon name="info" size={13} />
        </span>
      )}
    </div>
  )
}

function RuleSide({ side, rule, onChange, filters, lists, ownListId, listsMeta, links, linksMeta }: Props & { side: Side }) {
  const { t } = useTranslation('dynamicLists')
  const groups = rule[side]
  const firstType: FilterType = side === 'include' ? 'subscribed' : 'blocked'
  return (
    <section className={`dl-side dl-side--${side}`} aria-label={t(`editor.${side}`)}>
      <header className="dl-side-head">
        <h3 className="dl-h3">{t(`editor.${side}`)}</h3>
        <span className="dl-muted">{t(`editor.${side}Hint`)}</span>
      </header>
      {groups.map((group, g) => (
        <div key={g} className="dl-group-wrap">
          {g > 0 && <div className="dl-or">{t('editor.or')}</div>}
          <div className="dl-group">
            {group.filters.map((f, i) => (
              <div key={i} className="dl-filter-wrap">
                {i > 0 && <span className="dl-and">{t('editor.and')}</span>}
                <FilterRow
                  filter={f}
                  filters={filters}
                  lists={lists}
                  ownListId={ownListId}
                  listsMeta={listsMeta}
                  links={links ?? null}
                  linksMeta={linksMeta}
                  onChange={(next) => onChange(edit.setFilter(rule, side, g, i, next))}
                  onRemove={() => onChange(edit.removeFilter(rule, side, g, i))}
                />
              </div>
            ))}
            <button type="button" className="dl-link" onClick={() => onChange(edit.addFilter(rule, side, g, firstType))}>
              <FeatherIcon name="plus" size={13} /> {t('editor.addFilter')}
            </button>
          </div>
        </div>
      ))}
      <button type="button" className="dl-btn dl-btn--small" onClick={() => onChange(edit.addGroup(rule, side, firstType))}>
        <FeatherIcon name="plus" size={13} /> {side === 'include' ? t('editor.addGroup') : t('editor.addExclude')}
      </button>
    </section>
  )
}

function FilterRow({
  filter,
  filters,
  lists,
  ownListId,
  listsMeta,
  links,
  linksMeta,
  onChange,
  onRemove,
}: {
  filter: Filter
  filters: FilterInfo[]
  lists: ListOption[] | null
  ownListId?: string | null
  listsMeta?: ListsMeta
  links: ListOption[] | null
  linksMeta?: ListsMeta
  onChange: (f: Filter) => void
  onRemove: () => void
}) {
  const { t } = useTranslation('dynamicLists')
  const info = new Map(filters.map((f) => [f.type, f]))
  const help = t(`filterHelp.${filter.type}`, { defaultValue: '' })
  return (
    <div className="dl-filter">
      <Select
        className="dl-select--type"
        value={filter.type}
        label={t('filter.' + filter.type)}
        title={help || undefined}
        onChange={(type) => onChange(defaultFilter(type))}
        options={FILTER_ORDER.map((type) => {
          const available = info.get(type)?.available !== false
          return {
            value: type,
            label: available ? t(`filter.${type}`) : `${t(`filter.${type}`)} (${t('editor.comingSoon')})`,
            disabled: !available && type !== filter.type,
          }
        })}
      />
      <FilterParams filter={filter} lists={lists} ownListId={ownListId} listsMeta={listsMeta} links={links} linksMeta={linksMeta} onChange={onChange} />
      <button type="button" className="dl-iconbtn dl-filter-x" onClick={onRemove} aria-label={t('editor.removeFilter')}>
        <FeatherIcon name="x" size={14} />
      </button>
    </div>
  )
}

function FilterParams({
  filter: f,
  lists,
  ownListId,
  listsMeta,
  links,
  linksMeta,
  onChange,
}: {
  filter: Filter
  lists: ListOption[] | null
  ownListId?: string | null
  listsMeta?: ListsMeta
  links: ListOption[] | null
  linksMeta?: ListsMeta
  onChange: (f: Filter) => void
}) {
  const { t } = useTranslation('dynamicLists')
  if (BOOLEAN_FILTERS.includes(f.type) && 'value' in f) {
    return (
      <div className="dl-seg" role="group">
        {[true, false].map((v) => (
          <button key={String(v)} type="button" className={`dl-seg-btn ${f.value === v ? 'is-on' : ''}`} aria-pressed={f.value === v} onClick={() => onChange({ ...f, value: v })}>
            {v ? t('param.is') : t('param.isNot')}
          </button>
        ))}
      </div>
    )
  }
  switch (f.type) {
    case 'account_spend':
    case 'agency_spend':
      return (
        <div className="dl-params">
          <MoneyInput label={t('param.moreThan')} value={f.more_than} onChange={(v) => onChange({ ...f, more_than: v })} />
          <MoneyInput label={t('param.upTo')} value={f.up_to} onChange={(v) => onChange({ ...f, up_to: v })} />
          <PeriodSelect value={f.period} onChange={(period) => onChange({ ...f, period })} />
        </div>
      )
    case 'active_chat':
      return (
        <div className="dl-params">
          <PeriodSelect value={f.period} onChange={(period) => onChange({ ...f, period })} />
        </div>
      )
    case 'subscribed_within':
      return (
        <div className="dl-params">
          <span className="dl-num-box dl-num-box--days">
            <input
              type="number"
              min={1}
              max={3650}
              step={1}
              value={Number.isFinite(f.days) ? f.days : ''}
              aria-label={t('param.days')}
              onChange={(e) => onChange({ ...f, days: Number(e.target.value) })}
            />
            <span className="dl-muted">{t('param.days')}</span>
          </span>
        </div>
      )
    case 'in_list':
      return <ListPicker value={f.list_ids} lists={lists} ownListId={ownListId} meta={listsMeta} onChange={(list_ids) => onChange({ ...f, list_ids })} />
    case 'tracking_link':
      return (
        <div className="dl-params">
          <ListPicker
            value={linkKeysOf(f)}
            lists={links}
            meta={linksMeta}
            words={{
              pick: t('param.pickTrackingLinks'),
              none: t('param.noTrackingLinks'),
              loading: t('param.trackingLinksLoading'),
              error: t('param.trackingLinksError'),
              syncing: t('param.trackingLinksSyncing'),
            }}
            onChange={(keys) => onChange(withLinkKeys(f, keys))}
          />
          <FirstSeenSelect
            value={f.first_seen_days ?? null}
            onChange={(days) => {
              const next = { ...f }
              if (days == null) delete next.first_seen_days
              else next.first_seen_days = days
              onChange(next)
            }}
          />
        </div>
      )
    case 'buying_power':
      return (
        <div className="dl-params">
          <StarSelect value={f.min_stars} label={t('param.moreThan')} onChange={(v) => onChange({ ...f, min_stars: v })} />
          <span className="dl-muted">–</span>
          <StarSelect value={f.max_stars} label={t('param.upTo')} onChange={(v) => onChange({ ...f, max_stars: v })} />
        </div>
      )
    case 'ppv_sent':
      return (
        <div className="dl-params">
          <CountInput label="≥" value={f.at_least} onChange={(v) => onChange({ ...f, at_least: v })} />
          <CountInput label="≤" value={f.at_most} onChange={(v) => onChange({ ...f, at_most: v })} />
        </div>
      )
    default:
      return null
  }
}

function numberOrNull(raw: string): number | null {
  if (raw.trim() === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function MoneyInput({ label, value, onChange }: { label: string; value: number | null; onChange: (v: number | null) => void }) {
  const { t } = useTranslation('dynamicLists')
  return (
    <label className="dl-num">
      <span className="dl-num-label">{label}</span>
      <span className="dl-num-box">
        <span className="dl-muted">$</span>
        <input
          type="number"
          min={0}
          step="any"
          inputMode="decimal"
          value={value ?? ''}
          placeholder={t('param.any')}
          onChange={(e) => onChange(numberOrNull(e.target.value))}
        />
      </span>
    </label>
  )
}

function CountInput({ label, value, onChange }: { label: string; value: number | null; onChange: (v: number | null) => void }) {
  return (
    <label className="dl-num">
      <span className="dl-num-label">{label}</span>
      <span className="dl-num-box">
        <input type="number" min={0} step={1} value={value ?? ''} onChange={(e) => onChange(numberOrNull(e.target.value))} />
      </span>
    </label>
  )
}

function StarSelect({ value, label, onChange }: { value: number; label: string; onChange: (v: number) => void }) {
  return (
    <Select
      value={String(value)}
      label={label}
      onChange={(v) => onChange(Number(v))}
      options={['0', '1', '2', '3', '4', '5'].map((n) => ({ value: n, label: `${n}★` }))}
    />
  )
}

function PeriodSelect({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  const { t } = useTranslation('dynamicLists')
  const preset = value.type === 'all_time' ? 'all' : PERIOD_PRESETS.includes(value.days) ? String(value.days) : 'custom'
  const [custom, setCustom] = useState(preset === 'custom')
  const shownPreset = custom ? 'custom' : preset
  return (
    <span className="dl-period">
      <Select
        label={t('param.period')}
        value={shownPreset}
        onChange={(v) => {
          setCustom(v === 'custom')
          if (v === 'all') onChange({ type: 'all_time' })
          else if (v === 'custom') onChange({ type: 'last_days', days: value.type === 'last_days' ? value.days : 14 })
          else onChange({ type: 'last_days', days: Number(v) })
        }}
        options={[
          { value: 'all', label: t('period.allTime') },
          ...PERIOD_PRESETS.map((d) => ({ value: String(d), label: t('period.lastDays', { count: d }) })),
          { value: 'custom', label: t('period.custom') },
        ]}
      />
      {shownPreset === 'custom' && value.type === 'last_days' && (
        <span className="dl-num-box dl-num-box--days">
          <input
            type="number"
            min={1}
            max={3650}
            step={1}
            value={Number.isFinite(value.days) ? value.days : ''}
            aria-label={t('param.days')}
            onChange={(e) => onChange({ type: 'last_days', days: Number(e.target.value) })}
          />
          <span className="dl-muted">{t('param.days')}</span>
        </span>
      )}
    </span>
  )
}

/** "First seen" for Tracking link: any time, or a window of days. */
function FirstSeenSelect({ value, onChange }: { value: number | null; onChange: (days: number | null) => void }) {
  const { t } = useTranslation('dynamicLists')
  const preset = value == null ? 'any' : PERIOD_PRESETS.includes(value) ? String(value) : 'custom'
  const [custom, setCustom] = useState(preset === 'custom')
  const shown = custom ? 'custom' : preset
  return (
    <span className="dl-period">
      <Select
        label={t('param.firstSeen')}
        value={shown}
        onChange={(v) => {
          setCustom(v === 'custom')
          if (v === 'any') onChange(null)
          else if (v === 'custom') onChange(value ?? 14)
          else onChange(Number(v))
        }}
        options={[
          { value: 'any', label: `${t('param.firstSeen')}: ${t('param.firstSeenAny')}` },
          ...PERIOD_PRESETS.map((d) => ({ value: String(d), label: `${t('param.firstSeen')}: ${t('period.lastDays', { count: d })}` })),
          { value: 'custom', label: t('period.custom') },
        ]}
      />
      {shown === 'custom' && value != null && (
        <span className="dl-num-box dl-num-box--days">
          <input type="number" min={1} max={3650} step={1} value={Number.isFinite(value) ? value : ''} aria-label={t('param.days')}
            onChange={(e) => onChange(Number(e.target.value))} />
          <span className="dl-muted">{t('param.days')}</span>
        </span>
      )}
    </span>
  )
}

function ListPicker({
  value,
  lists,
  ownListId,
  meta,
  words,
  onChange,
}: {
  value: string[]
  lists: ListOption[] | null
  ownListId?: string | null
  meta?: ListsMeta
  /** Labels when the options are not OnlyFans lists (tracking links). */
  words?: { pick: string; none: string; loading: string; error: string; syncing?: string }
  onChange: (ids: string[]) => void
}) {
  const { t } = useTranslation('dynamicLists')
  const w = words ?? { pick: t('param.pickLists'), none: t('param.noLists'), loading: t('param.listsLoading'), error: t('param.listsError') }
  const name = (id: string) => lists?.find((l) => l.id === id)?.name ?? meta?.names?.[id] ?? `#${id}`
  const choices = (lists ?? []).filter((l) => l.id !== ownListId && !value.includes(l.id))
  return (
    <div className="dl-params dl-lists">
      {value.map((id) => (
        <button key={id} type="button" className="dl-chip" onClick={() => onChange(value.filter((x) => x !== id))}>
          <span className="dl-ellipsis">{name(id)}</span>
          <FeatherIcon name="x" size={12} />
        </button>
      ))}
      <Select
        value=""
        label={w.pick}
        placeholder={!lists ? (meta?.error ? w.error : '…') : choices.length ? w.pick : meta?.syncing && w.syncing ? w.syncing : w.none}
        disabled={!lists || choices.length === 0}
        title={!lists && meta?.error ? meta.error : undefined}
        onChange={(id) => onChange([...value, id])}
        options={choices.map((l) => ({ value: l.id, label: l.name }))}
      />
      {meta?.syncing && w.syncing && choices.length > 0 && <span className="dl-muted">{w.syncing}</span>}
    </div>
  )
}
